'use client';

/**
 * SysObeyaCohesionRecommendationsCard — stacked per-class refactoring
 * detail, worst first. The prose-heavy sibling of the high_cc
 * function-detail table: each class carries three paragraphs (issue /
 * recommendation / declarative alternative) plus badges — content
 * that doesn't fit a flat table, so it renders as detail sections,
 * mirroring the shape the legacy markdown report proved out.
 *
 * Slices `response.data[0].classes[]` + `totals.pattern_counts` +
 * `config` thresholds from the system_obeya_view cohesion call.
 *
 * Framework Pattern badge: the analyzer's "procedural by framework
 * design, not an actionable defect" verdict, rendered from the
 * honest `framework_mandated` boolean. The BFF bridges the two
 * analysis-schema generations into that one field (v2 → explicit
 * bool; v1 → derived from the retired cohesion_score==0 sentinel),
 * so the renderer reads one clean value and the raw cohesion_score
 * never reaches the frontend. This retires the loud-fail raw-
 * cohesion badge that stood in while the producer fix shipped.
 *
 * Severity treatment derives from `config` thresholds (0.6/0.8/0.9),
 * not hardcoded literals — the business rule ships on the response.
 *
 * Typography from `useTypographyBundle('canvas_dashboard')`; container
 * chrome (badges, section borders, chips) inline per the mode7
 * doctrine. All strings via `response.labels`.
 *
 * @see klyde_edge/ui_config/composition/cards/system_obeya.py — content_key="sys_obeya_cohesion_recommendations"
 * @see klyde_edge/ui_config/labels/system_obeya.py — cohesion_classes_table_title, cohesion_col_*, cohesion_pattern_*
 */

import { useMemo } from 'react';
import { Wrench } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { type TypographyBundleSlots } from '@/lib/design/typography';
import { useCardTypography } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { useSystemObeyaView, type SystemObeyaLabels } from './use-system-obeya-view';

interface Props {
  view_name?: string;
}

interface CohesionClass {
  class_name: string;
  procedural_score: number | null;
  // The BFF bridges the v1 cohesion_score sentinel + the v2 explicit
  // bool into this one honest field (see system_obeya/queries.py).
  // The raw cohesion_score no longer leaves the BFF.
  framework_mandated: boolean | null;
  pattern: string | null;
  issue: string | null;
  recommendation: string | null;
  declarative_alternative: string | null;
  effort: string | null;
  location: string | null;
  package: string;
  package_display_name: string;
  [k: string]: unknown;
}

export function SysObeyaCohesionRecommendationsCard({ view_name = 'cohesion' }: Props) {
  const { data, isLoading, queryKey } = useSystemObeyaView(view_name);
  const T = useCardTypography();
  const L: SystemObeyaLabels = data?.labels ?? {};

  const first = (data?.data ?? [])[0] as { classes?: CohesionClass[] } | undefined;
  const classes = first?.classes ?? [];
  const patternCounts = (data?.totals?.pattern_counts ?? {}) as Record<string, number>;
  const cfg = (data?.config ?? {}) as { minimum_score?: number; high?: number; critical?: number };

  const title = L.cohesion_classes_table_title ?? '';
  const patternLabel = (p: string | null) =>
    (p && (L[`cohesion_pattern_${p}`] as string)) || (p ?? '');

  // Card export — markdown mirrors the legacy report's per-class
  // sections; scene json ships the painted section content.
  useCardExport(useMemo(() => {
    const fw = L.cohesion_framework_pattern ?? 'Framework Pattern';
    const sections = classes.map((c, i) => {
      const badges = [
        `${L.cohesion_col_score ?? 'Score'}: ${c.procedural_score ?? ''}`,
        `${L.cohesion_col_pattern ?? 'Pattern'}: ${patternLabel(c.pattern)}`,
        `${L.cohesion_col_effort ?? 'Effort'}: ${c.effort ?? ''}`,
        ...(c.framework_mandated ? [fw] : []),
      ].join(' · ');
      return [
        `### ${i + 1}. ${c.class_name} (${c.package_display_name})`,
        badges,
        c.location ? `\`${c.location}\`` : '',
        c.issue ? `**${L.cohesion_col_issue ?? 'Issue'}**: ${c.issue}` : '',
        c.recommendation ? `**${L.cohesion_col_recommendation ?? 'Recommendation'}**: ${c.recommendation}` : '',
        c.declarative_alternative ? `**${L.cohesion_col_alternative ?? 'Declarative Alternative'}**: ${c.declarative_alternative}` : '',
      ].filter(Boolean).join('\n\n');
    });
    const md = sections.join('\n\n---\n\n');
    // Scene = painted section content (the prose IS what's painted).
    const visibleSections = classes.map((c) => ({
      class_name: c.class_name,
      module: c.package_display_name,
      score: c.procedural_score != null ? String(c.procedural_score) : '',
      framework_mandated: !!c.framework_mandated,
      pattern: patternLabel(c.pattern),
      effort: c.effort ?? '',
      issue: c.issue ?? '',
      recommendation: c.recommendation ?? '',
      declarative_alternative: c.declarative_alternative ?? '',
    }));
    return {
      label: title,
      text: md.replace(/^#+ /gm, '').replace(/\*\*/g, ''),
      markdown: md,
      json: { sections: visibleSections },
      content_key: 'sys_obeya_cohesion_recommendations',
      surface: 'canvas' as const,
      // Status = paintable content, not the query flag.
      status: (data ? 'loaded' : 'loading') as 'loading' | 'loaded',
      query_key: queryKey,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(classes), JSON.stringify(L), title, data, queryKey]));

  if (isLoading || !data) {
    return (
      <CardShell>
        <div className="p-6">
          <div className={T.body}>{L.loading_message ?? ''}</div>
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell>
      {/* Header — title + anti-pattern distribution chips */}
      <div className="p-6 border-b border-border/50">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" />
              <h2 className={T.header}>{title}</h2>
            </div>
            <p className={cn(T.subtitle, 'mt-1')}>{L.cohesion_recommendations_subtitle ?? ''}</p>
          </div>
          <div className="flex flex-wrap gap-1.5 justify-end shrink-0">
            {Object.entries(patternCounts).map(([p, n]) => (
              <span
                key={p}
                className={cn(T.metadata, 'px-2 py-1 rounded-md bg-muted font-medium whitespace-nowrap')}
              >
                {patternLabel(p)} · {n}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Stacked per-class detail sections */}
      <div className="overflow-auto flex-1 divide-y divide-border/40">
        {classes.map((c, idx) => (
          <ClassSection key={`${c.package}-${c.class_name}`} c={c} idx={idx} T={T} L={L} cfg={cfg} patternLabel={patternLabel} />
        ))}
      </div>
    </CardShell>
  );
}

// ─── One class's detail section ─────────────────────────────────────

function ClassSection({
  c,
  idx,
  T,
  L,
  cfg,
  patternLabel,
}: {
  c: CohesionClass;
  idx: number;
  T: TypographyBundleSlots;
  L: SystemObeyaLabels;
  cfg: { minimum_score?: number; high?: number; critical?: number };
  patternLabel: (p: string | null) => string;
}) {
  const score = c.procedural_score ?? 0;
  // Severity tint from config thresholds — the rule ships on the
  // response; the renderer just applies it.
  const scoreTint =
    cfg.critical != null && score >= cfg.critical
      ? 'bg-destructive/15 text-destructive'
      : cfg.high != null && score >= cfg.high
        ? 'bg-orange-500/15 text-orange-600 dark:text-orange-400'
        : 'bg-primary/10 text-primary';

  return (
    <div className="px-6 py-4">
      {/* Identity row — index, class, module, badges */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn(T.metadata, 'tabular-nums')}>{idx + 1}.</span>
            <span className={cn(T.name, 'font-mono')}>{c.class_name}</span>
            <span className={cn(T.metadata)}>{c.package_display_name}</span>
          </div>
          {c.location && (
            <div className={cn(T.metadata, 'font-mono mt-0.5 truncate')} title={c.location}>
              {c.location}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap shrink-0">
          <span className={cn(T.metadata, 'px-2 py-0.5 rounded-md font-semibold tabular-nums', scoreTint)}>
            {c.procedural_score ?? (L.dash_placeholder ?? '—')}
          </span>
          {c.pattern && (
            <span className={cn(T.metadata, 'px-2 py-0.5 rounded-md bg-muted font-medium')}>
              {patternLabel(c.pattern)}
            </span>
          )}
          {c.effort && (
            <span className={cn(T.metadata, 'px-2 py-0.5 rounded-md bg-muted')}>
              {c.effort}
            </span>
          )}
          {/* Framework Pattern — the analyzer's "procedural by
              framework design, not an actionable defect" verdict,
              now an honest boolean (was the cohesion_score==0
              sentinel; see queries.py bridge). The loud-fail raw
              cohesion badge is retired. */}
          {c.framework_mandated && (
            <span className={cn(T.metadata, 'px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground font-medium')}>
              {L.cohesion_framework_pattern ?? ''}
            </span>
          )}
        </div>
      </div>

      {/* Prose block — Issue / Recommendation / Declarative Alternative */}
      <dl className="mt-3 space-y-2">
        {c.issue && (
          <ProseRow label={L.cohesion_col_issue ?? ''} text={c.issue} T={T} />
        )}
        {c.recommendation && (
          <ProseRow label={L.cohesion_col_recommendation ?? ''} text={c.recommendation} T={T} accent />
        )}
        {c.declarative_alternative && (
          <ProseRow label={L.cohesion_col_alternative ?? ''} text={c.declarative_alternative} T={T} />
        )}
      </dl>
    </div>
  );
}

function ProseRow({
  label,
  text,
  T,
  accent,
}: {
  label: string;
  text: string;
  T: TypographyBundleSlots;
  accent?: boolean;
}) {
  return (
    <div className="flex gap-3">
      <dt className={cn(T.metadata, 'font-semibold uppercase tracking-wide shrink-0 w-28 pt-0.5')}>
        {label}
      </dt>
      <dd className={cn(T.body, accent && 'text-foreground font-medium')}>{text}</dd>
    </div>
  );
}

function CardShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-border bg-card shadow-sm h-full overflow-hidden flex flex-col">{children}</div>;
}
